import React, { useState } from 'react';
import { SmilePlus, Trash2, CornerUpLeft } from 'lucide-react';
import DeleteButton from '../ui/DeleteButton';
import LinkPreviewCard from '../LinkPreviewCard';
import ReactionDetailsPopup from './ReactionDetailsPopup';

const REACTIONS = ['👍', '❤️', '😂', '😮', '😢'];

// True on mouse/trackpad devices; false on touch-primary devices (phones/tablets).
// Controls whether hover action icons are shown — touch devices use the long-press drawer.
// Using pointer-capability media query instead of a CSS breakpoint so the icons appear
// on narrow desktop windows where no touch fallback exists.
const CAN_HOVER =
  typeof window !== 'undefined' &&
  window.matchMedia('(hover: hover) and (pointer: fine)').matches;

/**
 * Shared message row for both 1-on-1 (ChatPage) and service discussion (ServiceChatTab).
 *
 * Required props:
 *   msg, isMe, canDelete, userId
 *   pickerMsgId, onSetPickerMsgId
 *   onReact, onDelete
 *   onLongPressStart, onLongPressCancel
 *   bubbleContent    - React node (text + timestamp, rendered by parent)
 *   testIdPrefix     - prefix for data-testid (default: 'message')
 *
 * Optional props:
 *   onReply          - (snapshot) => void — shows Reply in desktop hover actions
 *   resolvedSenderName - display name for reply snapshot fallback
 *   senderLabel      - React node above bubble (group chat sender name)
 *   leftDecorator    - React node on far left (avatar in service chat)
 *   rightDecorator   - React node on far right (avatar in service chat)
 *   maxWidth         - CSS max-width of bubble column (default: '75%')
 *
 * Mobile UX note:
 *   Hover action icons are hidden on mobile (<md) — users use the long-press drawer instead.
 *   This prevents the hover icons from appearing alongside the drawer on touch devices.
 */
const ChatMessageBubble = ({
  msg,
  isMe,
  canDelete,
  userId,
  pickerMsgId,
  onSetPickerMsgId,
  onReact,
  onDelete,
  onLongPressStart,
  onLongPressCancel,
  bubbleContent,
  testIdPrefix = 'message',
  onReply,
  resolvedSenderName,
  senderLabel,
  leftDecorator,
  rightDecorator,
  maxWidth = '75%',
  memberMap = {},
}) => {
  const reactions = msg.reactions || {};
  const hasReactions = !msg.deleted && Object.values(reactions).some(u => u.length > 0);
  const [showReactionDetails, setShowReactionDetails] = useState(false);

  const handleReplyClick = () => {
    if (!onReply) return;
    onReply({
      message_id: msg.message_id,
      sender_name: resolvedSenderName || msg.sender_name || 'Unknown',
      text: (msg.text || '').slice(0, 100),
    });
  };

  return (
    <div
      className={`group flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}
      style={hasReactions ? { marginBottom: '30px' } : undefined}
    >
      {/* Left decorator (e.g. other user's avatar) */}
      {leftDecorator}

      {/* Side action icons — LEFT of bubble for isMe — desktop only (hidden on mobile) */}
      {isMe && !msg.deleted && !msg.pending && (
        <div
          className={`flex-shrink-0 self-center ${CAN_HOVER ? 'flex' : 'hidden'} items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-100 pointer-events-none group-hover:pointer-events-auto`}
          onClick={e => e.stopPropagation()}
        >
          {canDelete && (
            <DeleteButton
              onConfirm={() => onDelete(msg.message_id)}
              className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700"
              iconSize="w-4 h-4"
              title="Delete"
              testId={`delete-btn-${msg.message_id}`}
            />
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onSetPickerMsgId(prev => prev === msg.message_id ? null : msg.message_id); }}
            data-testid={`react-btn-${msg.message_id}`}
            className="p-1 rounded-full text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            title="React"
          >
            <SmilePlus className="w-4 h-4" />
          </button>
          {onReply && (
            <button
              onClick={handleReplyClick}
              data-testid={`reply-btn-${msg.message_id}`}
              className="p-1 rounded-full text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              title="Reply"
            >
              <CornerUpLeft className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Bubble column */}
      <div
        className={`min-w-0 flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
        style={{ maxWidth }}
      >
        {senderLabel}

        {/* ── Messenger-style reply quote ──
            Shown ABOVE the main bubble as a separate lighter element.
            A thin vertical line visually connects it to the bubble below. */}
        {msg.reply_to && !msg.deleted && (
          <div className={`flex items-stretch gap-1.5 w-full mb-0.5 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
            <div className={`w-0.5 self-stretch rounded-full flex-shrink-0 ${isMe ? 'bg-green-400/60' : 'bg-slate-300 dark:bg-slate-500'}`} />
            <div className={`flex-1 min-w-0 px-3 py-1.5 rounded-2xl text-xs ${
              isMe
                ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200'
                : 'bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300'
            }`}>
              <p className={`font-semibold text-[10px] mb-0.5 truncate ${isMe ? 'text-green-600 dark:text-green-400' : 'text-slate-500 dark:text-slate-400'}`}>
                {msg.reply_to.sender_name}
              </p>
              <p className="truncate">{msg.reply_to.text}</p>
            </div>
          </div>
        )}

        <div className="relative min-w-0 w-full">
          {/* Reaction picker popup */}
          {!msg.deleted && pickerMsgId === msg.message_id && (
            <div
              className={`absolute ${isMe ? 'right-0' : 'left-0'} bottom-full mb-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-full shadow-xl px-2.5 py-1.5 flex items-center gap-0.5 z-20`}
              onClick={(e) => e.stopPropagation()}
              data-testid={`reaction-picker-${msg.message_id}`}
            >
              {REACTIONS.map(emoji => {
                const alreadyReacted = (reactions[emoji] || []).includes(userId);
                return (
                  <button
                    key={emoji}
                    onClick={() => onReact(msg.message_id, emoji)}
                    data-testid={`reaction-emoji-${emoji}`}
                    className={`text-xl leading-none p-1.5 hover:scale-110 active:scale-95 transition-transform rounded-full ${
                      alreadyReacted
                        ? 'bg-green-100 dark:bg-green-800 ring-2 ring-green-500 dark:ring-green-400'
                        : 'opacity-70 hover:opacity-100'
                    }`}
                  >
                    {emoji}
                  </button>
                );
              })}
            </div>
          )}

          {/* Deleted bubble */}
          {msg.deleted ? (
            <div
              className="px-4 py-2.5 rounded-2xl bg-slate-100 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600"
              data-testid={`${testIdPrefix}-${msg.message_id}`}
            >
              <p className="text-sm italic text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
                <Trash2 className="w-3 h-3 flex-shrink-0" />
                This message was deleted
              </p>
            </div>
          ) : (
            /* Normal bubble */
            <div
              className={`px-4 py-2.5 rounded-2xl break-words ${hasReactions ? 'pb-4' : ''} ${
                isMe
                  ? 'bg-green-600 text-white rounded-br-md'
                  : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-600 rounded-bl-md'
              } ${msg.pending ? 'opacity-60' : ''}`}
              data-testid={`${testIdPrefix}-${msg.message_id}`}
              onTouchStart={(e) => onLongPressStart({ ...msg, canDelete }, e)}
              onTouchEnd={onLongPressCancel}
              onTouchMove={onLongPressCancel}
              onContextMenu={(e) => e.preventDefault()}
            >
              {bubbleContent}
            </div>
          )}

          {/* Link preview */}
          {!msg.deleted && msg.link_preview && (
            <LinkPreviewCard preview={msg.link_preview} />
          )}

          {/* Reaction badges — click opens the user-list popup */}
          {hasReactions && (
            <div className={`absolute bottom-[-14px] left-0 right-0 z-10 flex flex-wrap gap-1 ${isMe ? 'justify-start pl-1' : 'justify-end pr-1'}`}>
              {Object.entries(reactions)
                .filter(([, users]) => users.length > 0)
                .map(([emoji, users]) => (
                  <button
                    key={emoji}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.history.pushState({ reactionPopup: true }, '');
                      setShowReactionDetails(true);
                    }}
                    data-testid={`reaction-badge-${msg.message_id}-${emoji}`}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs border shadow-sm transition-colors bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 active:scale-95"
                  >
                    <span>{emoji}</span>
                    <span className="font-medium">{users.length}</span>
                  </button>
                ))
              }
            </div>
          )}

          {/* Reaction details popup */}
          {showReactionDetails && (
            <ReactionDetailsPopup
              reactions={reactions}
              memberMap={memberMap}
              currentUserId={userId}
              msgId={msg.message_id}
              onReact={onReact}
              onClose={() => setShowReactionDetails(false)}
            />
          )}
        </div>
      </div>

      {/* Side action icons — RIGHT of bubble for !isMe — desktop only (hidden on mobile) */}
      {!isMe && !msg.deleted && !msg.pending && (
        <div
          className={`flex-shrink-0 self-center ${CAN_HOVER ? 'flex' : 'hidden'} items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-100 pointer-events-none group-hover:pointer-events-auto`}
          onClick={e => e.stopPropagation()}
        >
          {onReply && (
            <button
              onClick={handleReplyClick}
              data-testid={`reply-btn-${msg.message_id}`}
              className="p-1 rounded-full text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              title="Reply"
            >
              <CornerUpLeft className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onSetPickerMsgId(prev => prev === msg.message_id ? null : msg.message_id); }}
            data-testid={`react-btn-${msg.message_id}`}
            className="p-1 rounded-full text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            title="React"
          >
            <SmilePlus className="w-4 h-4" />
          </button>
          {canDelete && (
            <DeleteButton
              onConfirm={() => onDelete(msg.message_id)}
              className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700"
              iconSize="w-4 h-4"
              title="Delete"
              testId={`delete-btn-${msg.message_id}`}
            />
          )}
        </div>
      )}

      {/* Right decorator (e.g. my avatar) */}
      {rightDecorator}
    </div>
  );
};

export default ChatMessageBubble;
